import React from 'react';
import TimeInputSlider from './TimeInputSlider';
import SelectSpeed from './SelectSpeed';

import {OptionsType, useEventContext} from 'providers/EventProvider';
import dayjs from 'dayjs';
import {Box, IconButton, Stack, Typography} from '@mui/material';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import SkipNextRoundedIcon from '@mui/icons-material/SkipNextRounded';
import SkipPreviousRoundedIcon from '@mui/icons-material/SkipPreviousRounded';

const SKIP_MS = 30 * 1000;

const Timer = () => {
  const {event, updateTime, options, updateOptions} = useEventContext();
  const eventStartTime = React.useMemo(() => new Date(event.startDate).getTime(), [event]);
  const eventEndTime = React.useMemo(() => new Date(event.endDate).getTime(), [event]);
  const minTime = 0;
  const maxTime = React.useMemo(() => eventEndTime - eventStartTime, [eventStartTime, eventEndTime]);

  const beginInterval = React.useRef<Date | undefined>(undefined);
  const startInterval = React.useRef<number | undefined>(undefined);
  const timer = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const count = React.useRef<number>(0);

  const [watchData, setWatchData] = React.useState<{clock: number}>({
    clock: minTime
  });
  const [tick, setTick] = React.useState<Date>();

  const stop = () => {
    timer.current && clearInterval(timer.current);
  };

  const update = (e: number) => {
    updateTime(e - event.delay * 1000);
  };

  const onChangeTime = (time: number) => {
    stop();
    update(time);
    if (!options.isStop) {
      start(time);
    } else {
      setWatchData((state) => ({...state, clock: time}));
    }
  };

  const start = (time: number) => {
    const interval = options.speed > 10 ? 1000 / options.speed : 100;
    beginInterval.current = new Date();
    startInterval.current = time;
    count.current = 0;
    stop();
    timer.current = setInterval(() => setTick(new Date()), interval);
    setWatchData((state) => ({...state, clock: time}));
  };

  const startTimer = () => {
    if (options.isStop) {
      options.isStop = false;
      updateOptions(options);
      start(watchData.clock);
    }
  };

  const stopTimer = () => {
    options.isStop = true;
    updateOptions(options);
    stop();
  };

  React.useEffect(() => {
    if (tick) {
      const now: Date = new Date();
      const time = startInterval.current! + options.speed * (now.getTime() - beginInterval.current!.getTime());
      setWatchData((state) => ({...state, clock: time}));
      if (options.speed > 10) {
        const fast = options.speed / 10;
        if (count.current % fast === 0) {
          update(time);
        }
        count.current++;
      } else {
        update(time);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  React.useEffect(() => {
    stop();
    if (!options.isStop) {
      start(watchData.clock);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.speed, options.isStop]);

  React.useEffect(() => {
    beginInterval.current = new Date();
    startInterval.current = minTime;
    count.current = 0;
    const now = new Date().getTime();
    const time = options.isLive ? now - eventStartTime : minTime;
    const newOptions = {...options};
    newOptions.speed = 10;
    if (options.isLive) {
      startInterval.current = time;
      newOptions.speed = 1;
    }
    updateOptions(newOptions);
    setWatchData({clock: minTime});

    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const newOptions: OptionsType = {...options};
    if (options.isLive) {
      beginInterval.current = new Date();
      const time = new Date().getTime() - eventStartTime;
      startInterval.current = time;
      count.current = 0;
      setWatchData((state) => ({...state, clock: time}));
      newOptions.speed = 1;
      newOptions.isStop = false;
    } else {
      setWatchData((state) => ({...state}));
      newOptions.speed = 10;
      newOptions.isStop = true;
    }
    updateOptions(newOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.isLive, options.liveSync]);

  const pauseLive = () => {
    stop();
    updateOptions({
      ...options,
      isLive: false,
      isStop: true
    });
  };

  const seek = (next: number) => {
    const upper = Math.max(maxTime, minTime);
    onChangeTime(Math.min(Math.max(next, minTime), upper));
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        flexShrink: 0,
        boxSizing: 'border-box',
        px: {xs: 1.5, md: 2},
        py: 0.5,
        minHeight: 48,
        minWidth: 0,
        bgcolor: '#fff',
        borderTop: '1px solid #f2f2f4',
        width: '100%'
      }}
    >
      <IconButton
        aria-label={options.isLive ? 'Pause live and replay' : options.isStop ? 'Play' : 'Pause'}
        onClick={options.isLive ? pauseLive : options.isStop ? startTimer : stopTimer}
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          bgcolor: 'primary.main',
          color: '#fff',
          '&:hover': {bgcolor: 'primary.dark'}
        }}
      >
        {options.isStop && !options.isLive ? <PlayArrowRoundedIcon /> : <PauseRoundedIcon />}
      </IconButton>
      <IconButton
        aria-label="Back 30 seconds"
        disabled={options.isLive}
        onClick={() => seek(watchData.clock - SKIP_MS)}
        sx={{color: '#2a2a2a', width: 32, height: 32}}
      >
        <SkipPreviousRoundedIcon />
      </IconButton>
      <IconButton
        aria-label="Forward 30 seconds"
        disabled={options.isLive}
        onClick={() => seek(watchData.clock + SKIP_MS)}
        sx={{color: '#2a2a2a', width: 32, height: 32}}
      >
        <SkipNextRoundedIcon />
      </IconButton>
      <SelectSpeed />
      <Box sx={{flex: '1 1 auto', minWidth: 48, px: 1.25, opacity: options.isLive ? 0.45 : 1}}>
        <TimeInputSlider
          minTime={minTime}
          maxTime={Math.max(maxTime, minTime)}
          value={watchData.clock}
          onChange={onChangeTime}
          disabled={options.isLive}
        />
      </Box>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{flexShrink: 0}}>
        <Typography sx={{color: '#9aa0a6', fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums'}}>
          {dayjs(eventStartTime + Math.max(watchData.clock, 0)).format('HH:mm:ss')}
        </Typography>
        {options.isLive && (
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0}} />
            <Typography sx={{color: 'primary.main', fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap'}}>Live</Typography>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default Timer;
